import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, IconButton, Alert, Stack, Tooltip,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard, tableHeadCellSx } from '../tabLayout';
import SearchableSelect from '../../common/SearchableSelect';
import { ToneChip } from '../../common/StatusChip';
import HelpTip, { HelpContent } from '../../common/HelpTip';
import { AddClassButtons, CopyToAllButton, QuietNote, SchoolClass, copyToAll, numSx, useClassNames } from './sectionParts';
import { AdmissionCycle, SeatMatrixEntry } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  classes: SchoolClass[];
  loadingClasses: boolean;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * Seat matrix editor. One row per class. The hard cap at offer-emission
 * time is `ceil(totalSeats * (1 + overbookPercent/100))` — see the backend's
 * seatEnforcement.ts for the race-safe mint. We show the computed hard cap
 * inline so the operator can sanity-check before saving.
 */

const HELP: Record<'waitlist' | 'overbooking' | 'cap', HelpContent> = {
  waitlist: {
    title: 'Waitlist',
    text: 'How many applicants can wait for a seat once the class is full. They move up when an offer lapses or is declined.',
    footnote: '0 means no limit.',
  },
  overbooking: {
    title: 'Overbooking',
    text: 'Extra offers, as a share of seats, because some families never take their seat.',
    footnote: '10% on 30 seats lets 33 offers go out.',
  },
  cap: {
    title: 'Seat cap',
    text: 'Seats plus the overbooking allowance. Offers for the class stop once it is reached.',
  },
};

const seatCap = (row: SeatMatrixEntry): number =>
  Math.ceil((row.totalSeats || 0) * (1 + ((row.overbookPercent ?? 0) / 100)));

const SeatMatrixSection: React.FC<Props> = ({ cycle, classes, loadingClasses, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<SeatMatrixEntry[]>([]);
  const { nameOf, options: classOptions } = useClassNames(classes);

  // Rebuild draft from cycle whenever edit mode opens.
  useEffect(() => {
    if (editing) setDraft(cycle.seatMatrix ?? []);
  }, [editing, cycle.seatMatrix]);

  const availableClasses = useMemo(() => {
    const used = new Set(draft.map(e => e.classId));
    return classes.filter(c => !used.has(c._id));
  }, [classes, draft]);

  const addRows = (add: SchoolClass[]) => setDraft(d => [
    ...d,
    ...add.map(c => ({ classId: c._id, totalSeats: 0, reservedQuotaSeats: 0, waitlistSize: 0, overbookPercent: 0 })),
  ]);

  const copyRow = (idx: number) =>
    setDraft(d => copyToAll(d, idx, r => ({ totalSeats: r.totalSeats, waitlistSize: r.waitlistSize, overbookPercent: r.overbookPercent })));

  const update = (idx: number, patch: Partial<SeatMatrixEntry>) => {
    setDraft(draft.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const remove = (idx: number) => {
    setDraft(draft.filter((_, i) => i !== idx));
  };

  const hasInvalid = draft.some(
    r => !r.classId || r.totalSeats < 0 || (r.overbookPercent ?? 0) < 0 || (r.overbookPercent ?? 0) > 100,
  );

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ seatMatrix: draft });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const rows = editing ? draft : (cycle.seatMatrix ?? []);
  const totalSeats = rows.reduce((s, r) => s + (r.totalSeats || 0), 0);
  const totalCap = rows.reduce((s, r) => s + seatCap(r), 0);

  return (
    <SectionCard
      title="Seat matrix"
      description="Seats available in each class, and how far offers may go past them."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDraft(cycle.seatMatrix ?? [])}
      saving={saving}
      canSave={!hasInvalid}
      headerActions={<AddClassButtons remaining={availableClasses} onAdd={addRows} disabled={loadingClasses} />}
    >
      {rows.length === 0 ? (
        <QuietNote>No seat limits yet — offers won't be capped for any class.</QuietNote>
      ) : (
        <TableCard
          footer={`${rows.length} ${rows.length === 1 ? 'class' : 'classes'} · ${totalSeats.toLocaleString()} seats · cap ${totalCap.toLocaleString()}`}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Class</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Seats</TableCell>
                <TableCell sx={tableHeadCellSx} align="right"><HelpTip label="Waitlist" {...HELP.waitlist} /></TableCell>
                <TableCell sx={tableHeadCellSx} align="right"><HelpTip label="Overbooking" {...HELP.overbooking} /></TableCell>
                <TableCell sx={tableHeadCellSx} align="right"><HelpTip label="Seat cap" {...HELP.cap} /></TableCell>
                {editing && <TableCell sx={tableHeadCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={`${row.classId}-${idx}`} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                  <TableCell>
                    {editing ? (
                      <SearchableSelect
                        label="Class"
                        value={row.classId}
                        onChange={(v) => update(idx, { classId: v })}
                        // Keep the row's own class selectable even though it's "used".
                        options={classOptions.filter(o => o.value === row.classId || availableClasses.some(c => c._id === o.value))}
                        clearable={false}
                        minWidth={160}
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body2" fontWeight={500}>{nameOf(row.classId)}</Typography>
                    )}
                  </TableCell>
                  <NumCell editing={editing} value={row.totalSeats} onChange={(v) => update(idx, { totalSeats: v })} />
                  <NumCell editing={editing} value={row.waitlistSize ?? 0} onChange={(v) => update(idx, { waitlistSize: v })} />
                  <NumCell editing={editing} value={row.overbookPercent ?? 0} onChange={(v) => update(idx, { overbookPercent: v })} suffix="%" max={100} />
                  <TableCell align="right">
                    <ToneChip label={seatCap(row).toLocaleString()} tone="neutral" sx={{ ...numSx, fontWeight: 600, minWidth: 40 }} />
                  </TableCell>
                  {editing && (
                    <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                      <Stack direction="row" justifyContent="flex-end">
                        {rows.length > 1 && <CopyToAllButton onClick={() => copyRow(idx)} />}
                        <Tooltip title="Remove row">
                          <IconButton size="small" color="error" onClick={() => remove(idx)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}
      {editing && hasInvalid && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <Typography variant="body2">
            All rows need a class, a non-negative total, and an overbook between 0 and 100.
          </Typography>
        </Alert>
      )}
    </SectionCard>
  );
};

const NumCell: React.FC<{
  editing: boolean; value: number; onChange: (v: number) => void;
  suffix?: string; max?: number;
}> = ({ editing, value, onChange, suffix, max }) => (
  <TableCell align="right">
    {editing ? (
      <TextField
        type="number"
        size="small"
        value={value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || '0', 10) || 0))}
        inputProps={{ min: 0, max, step: 1, style: { textAlign: 'right' } }}
        sx={{ width: 100 }}
      />
    ) : (
      <Typography variant="body2" sx={numSx} color={value ? 'text.primary' : 'text.disabled'}>
        {value.toLocaleString()}{suffix ?? ''}
      </Typography>
    )}
  </TableCell>
);

export default SeatMatrixSection;
